/**
 * React Query hooks for /api/v1/downhole/* — replaces the unrouted
 * `data/downholeData.ts`, which called `apiClient` directly, typed the OLD
 * table family, and dropped `meta` on the floor.
 *
 * EVERY HOOK RETURNS THE META. A downhole read is `{ data, meta }`, and a
 * screen cannot format a single measurement correctly without `meta.unit_system`
 * (dh_* stores imperial; a metric tenant reads converted values) or a single
 * money figure without the currency. So the hooks hand back both, and nothing
 * on a /downhole screen may assume either.
 *
 * EVERY HOOK SPREADS LOAD_FAILURE_QUERY_OPTIONS. Read the failure with
 * `queryLoadFailure(query)`, not `query.error` — a query waiting to retry
 * reports `error: null` (lib/load-failure.ts).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { setQueryDataMapped } from '../lib/queryClient';
import type {
  DhAssembly,
  DhAssemblyDetail,
  DhAssemblyWrite,
  DhTemplate,
  DhTemplateDetail,
  DhTemplateWrite,
  DhDisassembleWrite,
  DhDisassemblyResult,
  DhRate,
  DhRateWrite,
  DhRun,
  DhRunPrice,
  DhRunWrite,
  DhTicket,
  DhTicketDetail,
  DhTicketWrite,
  DhWorkOrderWrite,
  DhWorkOrder,
  DhGenerateInvoiceWrite,
  DhInvoice,
  DhInvoiceDetail,
  DhInvoiceSent,
  DhInvoiceRecipientPreview,
  DhTicketRecipientPreview,
  DhReport,
  DhReportMeta,
  DhComponent,
  DhComponentAlert,
  DhComponentDetail,
  DhComponentImportResult,
  DhComponentQr,
  DhComponentWrite,
  DhDashboard,
  DownholeMeta,
  DownholeResult,
} from '../types/downhole';

const EMPTY_META: DownholeMeta = { unit_system: null, currency: null, time_zone: null, today: null };

/**
 * Split a downhole body into its payload and its meta.
 *
 * `data: null` stays null — it is NOT replaced by the envelope, which is the
 * `res.data?.data || res.data` bug CLAUDE.md documents. A missing meta is
 * all-null, so a screen falls back to "as stored" and `—`, never to a guess.
 */
export function unwrapDownhole<T>(body: unknown): DownholeResult<T | null> {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    return { data: null, meta: EMPTY_META };
  }
  const b = body as { data?: unknown; meta?: Partial<DownholeMeta> };
  const data = b.data === undefined || b.data === null ? null : (b.data as T);
  return { data, meta: { ...EMPTY_META, ...(b.meta ?? {}) } };
}

/** A list read: a non-array payload becomes `[]` rather than reaching `.map`. */
export function unwrapDownholeList<T>(body: unknown): DownholeResult<T[]> {
  const { data, meta } = unwrapDownhole<unknown>(body);
  return { data: Array.isArray(data) ? (data as T[]) : [], meta };
}

export const downholeKeys = {
  all: ['downhole'] as const,
  dashboard: () => ['downhole', 'dashboard'] as const,
  components: (params?: Record<string, unknown>) => ['downhole', 'components', params ?? {}] as const,
  componentAlerts: () => ['downhole', 'components', 'alerts'] as const,
  component: (id: string) => ['downhole', 'component', id] as const,
  assemblies: (params?: Record<string, unknown>) => ['downhole', 'assemblies', params ?? {}] as const,
  assembly: (id: string) => ['downhole', 'assembly', id] as const,
  templates: (params?: Record<string, unknown>) => ['downhole', 'templates', params ?? {}] as const,
  template: (id: string) => ['downhole', 'template', id] as const,
  runs: (params?: Record<string, unknown>) => ['downhole', 'runs', params ?? {}] as const,
  assemblyRuns: (id: string) => ['downhole', 'runs', 'assembly', id] as const,
  workOrders: (params?: Record<string, unknown>) => ['downhole', 'work-orders', params ?? {}] as const,
  invoices: (params?: Record<string, unknown>) => ['downhole', 'invoices', params ?? {}] as const,
  invoice: (id: string) => ['downhole', 'invoice', id] as const,
  tickets: (params?: Record<string, unknown>) => ['downhole', 'tickets', params ?? {}] as const,
  ticket: (id: string) => ['downhole', 'ticket', id] as const,
  report: (report: string, params?: Record<string, unknown>) => ['downhole', 'report', report, params ?? {}] as const,
};

export function useDownholeDashboard() {
  return useQuery({
    queryKey: downholeKeys.dashboard(),
    queryFn: async () => unwrapDownhole<DhDashboard>((await api.downhole.dashboard()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeComponents(params?: { status?: string; category?: string; q?: string }) {
  return useQuery({
    queryKey: downholeKeys.components(params),
    queryFn: async () => unwrapDownholeList<DhComponent>((await api.downhole.components.list(params)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeComponentAlerts() {
  return useQuery({
    queryKey: downholeKeys.componentAlerts(),
    queryFn: async () => unwrapDownholeList<DhComponentAlert>((await api.downhole.components.alerts()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeComponent(id: string | undefined) {
  return useQuery({
    queryKey: downholeKeys.component(id ?? ''),
    queryFn: async () => unwrapDownhole<DhComponentDetail>((await api.downhole.components.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Component writes (api#900). A write answers with the component but no
 * screen caches it directly: the detail read carries histories the write does
 * not, so the detail is read again. The register, its alerts and the
 * dashboard's status counts all move with a new or retired tool.
 */
function useComponentWrite<V, R>(call: (vars: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => unwrapDownhole<R>((await call(vars)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['downhole', 'components'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'component'] });
      qc.invalidateQueries({ queryKey: downholeKeys.dashboard() });
    },
  });
}

export function useCreateComponent() {
  return useComponentWrite<DhComponentWrite, DhComponent>((body) => api.downhole.components.create(body));
}

export function useUpdateComponent() {
  return useComponentWrite<{ id: string; body: DhComponentWrite }, DhComponent>((v) => api.downhole.components.update(v.id, v.body));
}

export function useImportComponents() {
  return useComponentWrite<Record<string, unknown>[], DhComponentImportResult>((rows) => api.downhole.components.import(rows));
}

/** The printable label. Fetched only when asked for (`enabled`). */
export function useDownholeComponentQr(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['downhole', 'component', id ?? '', 'qr'] as const,
    queryFn: async () => unwrapDownhole<DhComponentQr>((await api.downhole.components.qr(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeAssemblies(params?: { status?: string; q?: string }) {
  return useQuery({
    queryKey: downholeKeys.assemblies(params),
    queryFn: async () => unwrapDownholeList<DhAssembly>((await api.downhole.assemblies.list(params)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeAssembly(id: string | undefined) {
  return useQuery({
    queryKey: downholeKeys.assembly(id ?? ''),
    queryFn: async () => unwrapDownhole<DhAssemblyDetail>((await api.downhole.assemblies.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** `{ active: 'true' }` for a picker that may only offer templates a build can start from. */
export function useDownholeTemplates(params?: { active?: 'true' | 'false' }) {
  return useQuery({
    queryKey: downholeKeys.templates(params),
    queryFn: async () => unwrapDownholeList<DhTemplate>((await api.downhole.templates.list(params)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeTemplate(id: string | null | undefined) {
  return useQuery({
    queryKey: downholeKeys.template(id ?? ''),
    queryFn: async () => unwrapDownhole<DhTemplateDetail>((await api.downhole.templates.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Every assembly write answers with the full assembly (api#883's loadAssembly),
 * so the response IS the new state: it is written straight into the assembly's
 * cache entry and the screen never keeps a second, local copy of the string.
 * (The unrouted builder did, behind an `initialized` flag, and never adopted
 * the server's answer — so a recomputed length or a refused add was invisible.)
 *
 * Component statuses move with the string in the same transaction, so the
 * component lists, the dashboard and the assembly lists are invalidated too.
 */
function useAssemblyWrite<V>(
  assemblyIdOf: (vars: V) => string | null,
  call: (vars: V) => Promise<{ data: unknown }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => unwrapDownhole<DhAssemblyDetail>((await call(vars)).data),
    onSuccess: (result, vars) => {
      const id = assemblyIdOf(vars) ?? result.data?.id ?? null;
      if (id && result.data) {
        const prev = qc.getQueryData<DownholeResult<DhAssemblyDetail | null>>(downholeKeys.assembly(id));
        if (prev?.meta?.unit_system !== undefined && prev.data) {
          // Already the unwrapped, typed shape the read caches. Writes carry
          // no meta, so keep the tenant settings the read brought.
          setQueryDataMapped<DownholeResult<DhAssemblyDetail | null>>(qc, downholeKeys.assembly(id), {
            data: result.data,
            meta: prev.meta,
          });
        } else {
          // No earlier read (a create): there is no tenant meta to keep, and
          // caching the write's empty meta would format a metric tenant's
          // assembly in inches. Read it instead.
          qc.invalidateQueries({ queryKey: downholeKeys.assembly(id) });
        }
      }
      qc.invalidateQueries({ queryKey: ['downhole', 'assemblies'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'components'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'component'] });
      qc.invalidateQueries({ queryKey: downholeKeys.dashboard() });
    },
  });
}

export function useCreateAssembly() {
  return useAssemblyWrite<DhAssemblyWrite>(() => null, (body) => api.downhole.assemblies.create(body));
}

export function useUpdateAssembly() {
  return useAssemblyWrite<{ id: string; body: DhAssemblyWrite }>((v) => v.id, (v) => api.downhole.assemblies.update(v.id, v.body));
}

export function useAssemblyTransition() {
  return useAssemblyWrite<{ id: string; action: 'ready' | 'dispatch' | 'return' }>(
    (v) => v.id,
    (v) => api.downhole.assemblies.transition(v.id, v.action),
  );
}

export function useAddAssemblyComponent() {
  return useAssemblyWrite<{ id: string; component_id: string; position?: number; makeup_torque?: number | null }>(
    (v) => v.id,
    ({ id, ...body }) => api.downhole.assemblies.addComponent(id, body),
  );
}

export function useReorderAssembly() {
  return useAssemblyWrite<{ id: string; order: { component_id: string; position: number }[] }>(
    (v) => v.id,
    (v) => api.downhole.assemblies.reorder(v.id, v.order),
  );
}

export function useRemoveAssemblyComponent() {
  return useAssemblyWrite<{ id: string; componentId: string }>(
    (v) => v.id,
    (v) => api.downhole.assemblies.removeComponent(v.id, v.componentId),
  );
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DhTemplateWrite) =>
      unwrapDownhole<DhTemplateDetail>((await api.downhole.templates.create(body)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['downhole', 'templates'] }),
  });
}

/** Archive (`active: false`) or restore a template (api#902). Lists and the detail are read again. */
export function useSetTemplateActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) =>
      unwrapDownhole<DhTemplateDetail>(
        (await (active ? api.downhole.templates.restore(id) : api.downhole.templates.archive(id))).data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['downhole', 'templates'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'template'] });
    },
  });
}

/**
 * Delete a building assembly (api#902). Its string is released to available,
 * so the component reads move too; the assembly's own cache entry is dropped
 * rather than read again into a 404.
 */
export function useDeleteAssembly() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.downhole.assemblies.remove(id)).data,
    onSuccess: (_r, id) => {
      qc.removeQueries({ queryKey: downholeKeys.assembly(id) });
      qc.invalidateQueries({ queryKey: ['downhole', 'assemblies'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'components'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'component'] });
      qc.invalidateQueries({ queryKey: downholeKeys.dashboard() });
    },
  });
}

/**
 * One run (`GET /runs/:id`), for the run's own page. Keyed under
 * ['downhole', 'runs'] so every run write's invalidation refreshes it too.
 */
export function useDownholeRun(id: string | undefined) {
  return useQuery({
    queryKey: ['downhole', 'runs', 'one', id ?? ''] as const,
    queryFn: async () => unwrapDownhole<DhRun>((await api.downhole.runs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeRuns(params?: { assembly_id?: string; from?: string; to?: string }, enabled = true) {
  return useQuery({
    queryKey: downholeKeys.runs(params),
    queryFn: async () => unwrapDownholeList<DhRun>((await api.downhole.runs.list(params)).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Run writes (api#902). Closing, correcting or deleting a run moves every
 * member's run hours, runs and footage in the same transaction, and LIH retires
 * a tool — so the runs, the assembly, the component reads and the dashboard
 * are all read again.
 */
function useRunWrite<V, R>(call: (vars: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    // A field write. With React Query's default networkMode ('online') a write
    // made after the browser's `offline` event is PAUSED — no request, no
    // error, a spinner forever (memory: field-offline-save-hangs). 'always'
    // sends it, so a dead zone fails as an error the dialog shows. Runs are not
    // queued offline; failing loudly is the rule for a write that cannot be.
    networkMode: 'always',
    mutationFn: async (vars: V) => unwrapDownhole<R>((await call(vars)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['downhole', 'runs'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'assembly'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'assemblies'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'components'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'component'] });
      qc.invalidateQueries({ queryKey: downholeKeys.dashboard() });
    },
  });
}

export function useLogRun() {
  return useRunWrite<{ assemblyId: string; body: DhRunWrite }, DhRun>((v) => api.downhole.runs.create(v.assemblyId, v.body));
}

export function useUpdateRun() {
  return useRunWrite<{ id: string; body: DhRunWrite }, DhRun>((v) => api.downhole.runs.update(v.id, v.body));
}

export function useDeleteRun() {
  return useRunWrite<string, { id: string; deleted: true }>((id) => api.downhole.runs.remove(id));
}

/**
 * Tear down a returned assembly (api#905). Every tool's status moves (stock,
 * shop, retired), work orders open, the assembly becomes disassembled — so
 * nearly every downhole read is read again.
 */
export function useDisassemble() {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always', // see useRunWrite: a field write fails loudly offline, never pauses
    mutationFn: async ({ id, body }: { id: string; body: DhDisassembleWrite }) =>
      unwrapDownhole<DhDisassemblyResult>((await api.downhole.assemblies.disassemble(id, body)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['downhole'] }),
  });
}

/** Work-order status and notes (api#905). Closing the last open order can move the tool. */
export function useUpdateWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always', // see useRunWrite: a field write fails loudly offline, never pauses
    mutationFn: async ({ id, body }: { id: string; body: DhWorkOrderWrite }) =>
      unwrapDownhole<DhWorkOrder>((await api.downhole.workOrders.update(id, body)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['downhole', 'work-orders'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'work-order'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'components'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'component'] });
      qc.invalidateQueries({ queryKey: downholeKeys.dashboard() });
    },
  });
}

/** Open a work order or inspection on a tool (api#905). The tool's status can move, so its reads go too. */
export function useOpenWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always', // see useRunWrite: a write fails loudly offline, never pauses
    mutationFn: async ({ componentId, kind, body }: { componentId: string; kind: 'inspection' | 'work_order'; body: DhWorkOrderWrite }) =>
      unwrapDownhole<DhWorkOrder>((await api.downhole.workOrders.open(componentId, kind, body)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['downhole', 'work-orders'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'components'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'component'] });
      qc.invalidateQueries({ queryKey: downholeKeys.dashboard() });
    },
  });
}

export function useDownholeWorkOrder(id: string | undefined) {
  return useQuery({
    queryKey: ['downhole', 'work-order', id ?? ''] as const,
    queryFn: async () => unwrapDownhole<DhWorkOrder>((await api.downhole.workOrders.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** `enabled: false` for a role the API refuses rates to: it would only draw a 403. */
export function useDownholeRates(
  params?: { customer_id?: string; category?: string; component_id?: string; on?: string; include_deleted?: 'true' },
  enabled = true,
) {
  return useQuery({
    queryKey: ['downhole', 'rates', params ?? {}] as const,
    queryFn: async () => unwrapDownholeList<DhRate>((await api.downhole.rates.list(params)).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Rate writes. A rate change re-prices every run that has not been invoiced, so prices are read again too. */
function useRateWrite<V>(call: (vars: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: V) => unwrapDownhole<DhRate>((await call(vars)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['downhole', 'rates'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'run-price'] });
    },
  });
}

export function useCreateRate() {
  return useRateWrite<DhRateWrite>((body) => api.downhole.rates.create(body));
}
export function useUpdateRate() {
  return useRateWrite<{ id: string; body: DhRateWrite }>((v) => api.downhole.rates.update(v.id, v.body));
}
export function useDeleteRate() {
  return useRateWrite<string>((id) => api.downhole.rates.remove(id));
}
export function useRestoreRate() {
  return useRateWrite<string>((id) => api.downhole.rates.restore(id));
}

/** One closed or not-run run's price (api#906). Only fetched when asked for. */
export function useDownholeRunPrice(runId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ['downhole', 'run-price', runId ?? ''] as const,
    queryFn: async () => unwrapDownhole<DhRunPrice>((await api.downhole.runs.price(runId as string)).data),
    enabled: !!runId && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Invoice writes (api#910). Generating or voiding moves runs on and off a live
 * invoice, so run reads (their invoiced state) are read again with the lists.
 * networkMode 'always': an invoice write made offline fails as an error, never
 * a paused request (memory: field-offline-save-hangs).
 */
function useInvoiceWrite<V, R>(call: (vars: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: async (vars: V) => unwrapDownhole<R>((await call(vars)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['downhole', 'invoices'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'invoice'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'runs'] });
      qc.invalidateQueries({ queryKey: downholeKeys.dashboard() });
      // api#1090: an invoice can bill a well job's days, and voiding one frees
      // them, so every job read (detail, days, billing) and the ticket lists
      // (which carry invoice_number) move with it. Prefix ['downhole','job'].
      qc.invalidateQueries({ queryKey: ['downhole', 'job'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'tickets'] });
    },
  });
}

export function useGenerateInvoice() {
  return useInvoiceWrite<DhGenerateInvoiceWrite, DhInvoice>((body) => api.downhole.invoices.generate(body));
}
export function useSendInvoice() {
  return useInvoiceWrite<{ id: string; cc?: string[] }, DhInvoiceSent>(({ id, ...body }) => api.downhole.invoices.send(id, body));
}

/** API-A: who an invoice send would go to. Fetched while the send dialog is open. */
export function useInvoiceRecipient(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['downhole', 'invoice', id ?? '', 'recipient'] as const,
    queryFn: async () => unwrapDownhole<DhInvoiceRecipientPreview>((await api.downhole.invoices.recipient(id as string)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** API-A: who a ticket send would go to, for what is typed (`email`, `contact_name`). */
export function useTicketRecipient(id: string | undefined, params: { email?: string; contact_name?: string }, enabled: boolean) {
  return useQuery({
    queryKey: ['downhole', 'ticket', id ?? '', 'recipient', params] as const,
    queryFn: async () => unwrapDownhole<DhTicketRecipientPreview>((await api.downhole.tickets.recipient(id as string, params)).data),
    enabled: !!id && enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}
export function useVoidInvoice() {
  return useInvoiceWrite<string, unknown>((id) => api.downhole.invoices.voidInvoice(id));
}

export function useDownholeAssemblyRuns(assemblyId: string | undefined) {
  return useQuery({
    queryKey: downholeKeys.assemblyRuns(assemblyId ?? ''),
    queryFn: async () => unwrapDownholeList<DhRun>((await api.downhole.runs.forAssembly(assemblyId as string)).data),
    enabled: !!assemblyId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeWorkOrders(params?: {
  status?: string;
  work_type?: string;
  open?: 'true';
  assigned_to?: string;
  unassigned?: 'true';
}) {
  return useQuery({
    queryKey: downholeKeys.workOrders(params),
    queryFn: async () => unwrapDownholeList<DhWorkOrder>((await api.downhole.workOrders.list(params)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeInvoices(params?: { status?: string; from?: string; to?: string }) {
  return useQuery({
    queryKey: downholeKeys.invoices(params),
    queryFn: async () => unwrapDownholeList<DhInvoice>((await api.downhole.invoices.list(params)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeInvoice(id: string | undefined) {
  return useQuery({
    queryKey: downholeKeys.invoice(id ?? ''),
    queryFn: async () => unwrapDownhole<DhInvoiceDetail>((await api.downhole.invoices.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** A report's rows and the range the API actually used (`meta.start_date` / `end_date`). */
export function useDownholeReport<Row>(report: DhReport, params?: { start_date?: string; end_date?: string }) {
  return useQuery({
    queryKey: downholeKeys.report(report, params),
    queryFn: async () => {
      const { data, meta } = unwrapDownholeList<Row>((await api.downhole.reports.get(report, params)).data);
      return { data, meta: meta as DhReportMeta };
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Field tickets (0461). The reads carry no `meta` — a ticket's money is in its
 * own `currency`, and a screen takes the tenant's zone from LocaleContext.
 * `enabled: false` for a role the API refuses tickets to (it would only draw a
 * 403).
 */
export function useDownholeTickets(params?: { assembly_id?: string; customer_id?: string; status?: string }, enabled = true) {
  return useQuery({
    queryKey: downholeKeys.tickets(params),
    queryFn: async () => unwrapDownholeList<DhTicket>((await api.downhole.tickets.list(params)).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useDownholeTicket(id: string | undefined) {
  return useQuery({
    queryKey: downholeKeys.ticket(id ?? ''),
    queryFn: async () => unwrapDownhole<DhTicketDetail>((await api.downhole.tickets.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Ticket writes. A ticket write moves its runs on and off it (dh_ticket_id,
 * ticket_status on every run read), so the run reads go with the ticket reads.
 * networkMode 'always': an office write made offline fails as an error, never
 * a paused request.
 */
function useTicketWrite<V, R>(call: (vars: V) => Promise<{ data: unknown }>) {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: async (vars: V) => {
      const body = (await call(vars)).data as Record<string, unknown> | null;
      return { ...unwrapDownhole<R>(body), raw: body };
    },
    // Settled, not only success: a refused write can still have moved the
    // ticket — a send whose email failed stamps customer_email_status 'failed'
    // and answers 502 — and the page must show that, not the status before.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['downhole', 'tickets'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'ticket'] });
      qc.invalidateQueries({ queryKey: ['downhole', 'runs'] });
      qc.invalidateQueries({ queryKey: downholeKeys.dashboard() });
    },
  });
}

export function useCreateTicket() {
  return useTicketWrite<DhTicketWrite, DhTicketDetail>((body) => api.downhole.tickets.create(body));
}
export function useUpdateTicket() {
  return useTicketWrite<{ id: string; body: DhTicketWrite }, DhTicketDetail>((v) => api.downhole.tickets.update(v.id, v.body));
}
export function useDeleteTicket() {
  return useTicketWrite<string, { id: string; deleted: true }>((id) => api.downhole.tickets.remove(id));
}
export function useOfficeApproveTicket() {
  return useTicketWrite<string, DhTicketDetail>((id) => api.downhole.tickets.officeApprove(id));
}
export function useSendTicket() {
  return useTicketWrite<{ id: string; email?: string; contact_name?: string; cc?: string[] }, DhTicketDetail>(({ id, ...body }) =>
    api.downhole.tickets.send(id, body),
  );
}
export function useRecordTicketApproval() {
  return useTicketWrite<{ id: string; form: FormData }, DhTicketDetail>((v) => api.downhole.tickets.recordApproval(v.id, v.form));
}
